import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';

import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { ToggleService } from '~/toggles/services/toggle.service';

import { RouteNameNearbyEnum } from './enums/nearby.enum';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  constructor(
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly redirectionService: RedirectionService,
    private readonly router: Router,
    private readonly activityService: OrganizerSharedActivityService,
    private readonly toggleService: ToggleService
  ) {}

  public static getCurrentName(activatedRoute: ActivatedRoute): string {
    return activatedRoute.snapshot.data.localizeRouter.path;
  }

  private searchRouteIndexByActivityType(
    nearby: RouteNameNearbyEnum,
    currentIndex: number,
    routeNames: RoutingInterface[]
  ): number | undefined {
    const activity = this.activityService.activity;
    const offset = nearby === RouteNameNearbyEnum.Next ? 1 : -1;
    const nearbyIndex = currentIndex + offset;
    const nearbyRouteName = routeNames[nearbyIndex];

    if (!nearbyRouteName) {
      return undefined;
    }

    if (
      !nearbyRouteName.activityTypes ||
      nearbyRouteName.activityTypes.includes(activity?.type)
    ) {
      if (!nearbyRouteName.excludeActivityTypes?.includes(activity?.type)) {
        return nearbyIndex;
      }

      return this.searchRouteIndexByActivityType(
        nearby,
        nearbyIndex,
        routeNames
      );
    }

    return this.searchRouteIndexByActivityType(nearby, nearbyIndex, routeNames);
  }

  public getCurrentPath(
    activatedRoute: ActivatedRoute,
    navigationExtras: NavigationExtras = {}
  ): string {
    return this.getPath(
      NavigationService.getCurrentName(activatedRoute),
      navigationExtras
    );
  }

  public getNearbyName(
    routeNames: RoutingInterface[],
    activatedRouteOrName: ActivatedRoute | string,
    nearby = RouteNameNearbyEnum.Next
  ): string {
    const filteredRouteNames = routeNames.filter(
      (routeName) =>
        !this.toggleService.isEnabled(routeName.toggleForDeactivation)
    );
    const name =
      activatedRouteOrName instanceof ActivatedRoute
        ? NavigationService.getCurrentName(activatedRouteOrName)
        : activatedRouteOrName;

    const currentIndex = filteredRouteNames.findIndex(
      (routeName) => routeName.routeName === name
    );

    if (currentIndex === -1) {
      return undefined;
    }

    const nearbyIndex = this.searchRouteIndexByActivityType(
      nearby,
      currentIndex,
      filteredRouteNames
    );

    return nearbyIndex !== undefined
      ? filteredRouteNames[nearbyIndex].routeName
      : undefined;
  }

  public getNextName(
    routeNames: RoutingInterface[],
    activatedRouteOrName: ActivatedRoute | string
  ): string {
    return this.getNearbyName(routeNames, activatedRouteOrName);
  }

  public getNextPath(
    routeNames: RoutingInterface[],
    activatedRoute: ActivatedRoute,
    navigationExtras: NavigationExtras = {}
  ): string {
    return this.getPath(
      this.getNextName(routeNames, activatedRoute),
      navigationExtras
    );
  }

  public getPath(
    currentName: string,
    navigationExtras: NavigationExtras = {}
  ): string {
    const localizedRoute = this.localizeRouterService.translateRoute(
      '/' + currentName
    );

    const urlTree = this.router.createUrlTree(
      typeof localizedRoute === 'string' ? [localizedRoute] : localizedRoute,
      navigationExtras
    );

    return this.router.serializeUrl(urlTree);
  }

  public getPreviousName(
    routeNames: RoutingInterface[],
    activatedRouteOrName: ActivatedRoute | string
  ): string {
    return this.getNearbyName(
      routeNames,
      activatedRouteOrName,
      RouteNameNearbyEnum.Previous
    );
  }

  public getPreviousPath(
    routeNames: RoutingInterface[],
    activatedRoute: ActivatedRoute,
    navigationExtras: NavigationExtras = {}
  ): string {
    return this.getPath(
      this.getPreviousName(routeNames, activatedRoute),
      navigationExtras
    );
  }

  public nextNavigate(
    routeNames: RoutingInterface[],
    activatedRouteOrName: ActivatedRoute | string,
    extras?: NavigationExtras
  ): Promise<boolean> {
    return this.redirectionService.redirect(
      this.getNextName(routeNames, activatedRouteOrName),
      extras
    );
  }

  public previousNavigate(
    routeNames: RoutingInterface[],
    activatedRouteOrName: ActivatedRoute | string,
    extras?: NavigationExtras
  ): Promise<boolean> {
    return this.redirectionService.redirect(
      this.getPreviousName(routeNames, activatedRouteOrName),
      extras
    );
  }
}
